/**
 * BeezFy Premium Mobile Menu System
 * Ultra-premium mobile navigation for fashion e-commerce
 * Optimized for conversion and mobile-first experience
 */
// Miniature: chiede al CDN Shopify la larghezza giusta invece della foto originale
// (le originali da ~20 megapixel mandano in crash Safari su iPhone).
window.bfyImg = window.bfyImg || function (u, w) {
  if (!u) return '';
  u = String(u);
  if (u.indexOf('cdn.shopify.com') < 0 && u.indexOf('/cdn/shop/') < 0) return u;
  u = u.replace(/([?&])width=[0-9]+(&|$)/, function (m, a, b) { return b ? a : ''; }).replace(/[?&]$/, '');
  return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
};


class BeezfyMobileMenuPremium {
  constructor() {
    this.state = {
      isOpen: false,
      activeSubmenu: null,
      activeNestedSubmenu: null,
      searchQuery: '',
      currentLevel: 0,
      history: [],
      touchStartX: 0,
      touchStartY: 0,
      isAnimating: false
    };

    // Load admin settings
    const adminSettings = window.beezfySettings?.mobileMenu || {};
    
    this.config = {
      enablePremium: adminSettings.enablePremium !== false,
      animationDuration: adminSettings.animationSpeed || 300,
      animationStyle: adminSettings.animationStyle || 'slide',
      swipeThreshold: adminSettings.swipeThreshold || 50,
      searchDebounce: 300,
      megaMenuThreshold: 5,
      lazyLoadDelay: 100,
      enableSearch: adminSettings.enableSearch !== false,
      enableLiveSearch: adminSettings.enableLiveSearch !== false,
      searchResultsLimit: adminSettings.searchResultsLimit || 5,
      enableRecentlyViewed: adminSettings.enableRecentlyViewed !== false,
      recentlyViewedLimit: adminSettings.recentlyViewedLimit || 4,
      enableSwipeGestures: adminSettings.enableSwipeGestures !== false,
      enableWishlistLink: adminSettings.enableWishlistLink !== false,
      enableAccountLinks: adminSettings.enableAccountLinks !== false,
      enableSocialLinks: adminSettings.enableSocialLinks !== false,
      enableCategoryImages: adminSettings.enableCategoryImages === true,
      enableDarkMode: adminSettings.enableDarkMode === true,
      maxDepth: parseInt(adminSettings.maxDepth) || 3
    };

    this.elements = {};
    this.handleResize = this.handleResize.bind(this);
    this.resizeTimer = null;

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    if (!this.config.enablePremium) return;
    
    this.cacheElements();
    this.enhanceMenuStructure();
    this.updateNavOffset();
    this.setupEventListeners();
    
    if (this.config.enableSwipeGestures) {
      this.setupTouchGestures();
    }
    
    if (this.config.enableSearch) {
      this.setupSearch();
    }
    
    if (this.config.enableAccountLinks) {
      this.setupAccountFeatures();
    }
    
    this.setupLocalization();
    this.initializeAccessibility();
    
    if (this.config.enableDarkMode) {
      this.setupDarkMode();
    }

    window.addEventListener('resize', this.handleResize);
  }

  cacheElements() {
    this.elements = {
      drawer: document.querySelector('#beezfy-menu-drawer'),
      overlay: document.querySelector('.beezfy-drawer-overlay'),
      header: document.querySelector('.beezfy-drawer__header'),
      content: document.querySelector('.beezfy-drawer__content'),
      nav: document.querySelector('.menu-drawer__nav'),
      search: document.querySelector('.menu-drawer__search'),
      searchInput: document.querySelector('.search-form__input'),
      searchResults: null,
      account: document.querySelector('.menu-drawer__account'),
      localization: document.querySelector('.menu-drawer__localization'),
      social: document.querySelector('.menu-drawer__social'),
      triggers: document.querySelectorAll('[data-drawer-trigger="menu"]'),
      closeBtn: document.querySelector('[data-drawer-close]'),
      backButton: null
    };
  }

  enhanceMenuStructure() {
    if (!this.elements.drawer) return;

    // Add premium classes
    this.elements.drawer.classList.add('beezfy-menu-premium');

    // Create back navigation button
    this.createBackButton();

    // Enhance menu items with premium features
    this.enhanceMenuItems();

    // Add search results container
    this.createSearchResults();

    // Add promotional banner if configured
    this.addPromotionalBanner();

    // Add recently viewed products
    this.addRecentlyViewed();
  }

  updateNavOffset() {
    if (!this.elements.drawer || !this.elements.nav) return;

    requestAnimationFrame(() => {
      const navOffset = this.elements.nav.offsetTop || 0;
      this.elements.drawer.style.setProperty('--menu-nav-offset', `${navOffset}px`);
    });
  }

  handleResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.updateNavOffset();
    }, 100);
  }

  createBackButton() {
    const backBtn = document.createElement('button');
    backBtn.className = 'menu-drawer__back';
    backBtn.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M19 12H5M5 12l7 7M5 12l7-7"/>
      </svg>
      <span data-back-text>${window.theme?.strings?.back || 'Back'}</span>
    `;
    backBtn.setAttribute('aria-label', 'Go back');

    this.elements.header.insertBefore(backBtn, this.elements.header.firstChild);
    this.elements.backButton = backBtn;
  }

  enhanceMenuItems() {
    const menuItems = this.elements.nav?.querySelectorAll('.menu-drawer__item');

    menuItems?.forEach((item, index) => {
      const hasSubmenu = item.querySelector('.menu-drawer__submenu');

      if (hasSubmenu) {
        // Add heading placeholder for submenu
        if (!hasSubmenu.querySelector('[data-submenu-heading]')) {
          const heading = document.createElement('div');
          heading.className = 'menu-drawer__submenu-heading';
          heading.setAttribute('data-submenu-heading', '');
          hasSubmenu.insertBefore(heading, hasSubmenu.firstChild);
        }

        // Add slide animation attributes
        item.setAttribute('data-menu-index', index);
        hasSubmenu.setAttribute('data-submenu-level', '1');
        hasSubmenu.setAttribute('aria-hidden', 'true');

        // Add image thumbnails for categories if available
        this.addCategoryImages(item);

        // Enhance nested submenus
        const nestedSubmenus = hasSubmenu.querySelectorAll('.menu-drawer__submenu--nested');
        nestedSubmenus.forEach((nested, nestedIndex) => {
          if (!nested.querySelector('[data-submenu-heading]')) {
            const heading = document.createElement('div');
            heading.className = 'menu-drawer__submenu-heading';
            heading.setAttribute('data-submenu-heading', '');
            nested.insertBefore(heading, nested.firstChild);
          }
          nested.setAttribute('data-submenu-level', '2');
          nested.setAttribute('data-nested-index', nestedIndex);
          nested.setAttribute('aria-hidden', 'true');
        });
      }

      // Add touch feedback
      item.addEventListener('touchstart', () => {
        item.classList.add('touch-active');
      });

      item.addEventListener('touchend', () => {
        setTimeout(() => item.classList.remove('touch-active'), 100);
      });
    });
  }

  addCategoryImages(menuItem) {
    if (!this.config.enableCategoryImages) return;
    
    const link = menuItem.querySelector('.menu-drawer__link');
    if (!link || !window.beezfySettings?.menuCategoryImages) return;

    const categoryName = link.textContent.trim().toLowerCase();
    const imageMap = window.beezfySettings.menuCategoryImages;

    if (imageMap[categoryName]) {
      const imgContainer = document.createElement('div');
      imgContainer.className = 'menu-drawer__item-image';
      const safeAlt = (link.textContent || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
      imgContainer.innerHTML = `<img src="${imageMap[categoryName]}" alt="${safeAlt}" loading="lazy">`;
      menuItem.insertBefore(imgContainer, menuItem.firstChild);
    }
  }

  createSearchResults() {
    const searchResults = document.createElement('div');
    searchResults.className = 'menu-drawer__search-results';
    searchResults.style.display = 'none';
    searchResults.innerHTML = `
      <div class="search-results__header">
        <span class="search-results__title">${window.theme?.strings?.searchResults || 'Search Results'}</span>
        <button class="search-results__close" aria-label="Close search">×</button>
      </div>
      <div class="search-results__content"></div>
    `;

    this.elements.search?.appendChild(searchResults);
    this.elements.searchResults = searchResults;
  }

  addPromotionalBanner() {
    if (!window.beezfySettings?.menuPromo) return;

    const banner = document.createElement('div');
    banner.className = 'menu-drawer__promo-banner';
    banner.innerHTML = `
      <div class="promo-banner__content">
        <span class="promo-banner__text">${window.beezfySettings.menuPromo.text}</span>
        ${window.beezfySettings.menuPromo.link ?
          `<a href="${window.beezfySettings.menuPromo.link}" class="promo-banner__link">
            ${window.beezfySettings.menuPromo.linkText || 'Shop Now'}
          </a>` : ''}
      </div>
    `;

    this.elements.content?.insertBefore(banner, this.elements.nav);
  }

  addRecentlyViewed() {
    if (!this.config.enableRecentlyViewed) return;

    let container = this.elements.drawer?.querySelector('[data-recently-viewed-target]') || this.elements.drawer?.querySelector('.menu-drawer__recently-viewed');

    if (!container) {
      container = document.createElement('div');
      container.className = 'menu-drawer__recently-viewed';
      this.elements.content?.appendChild(container);
    }

    const recentProducts = this.getRecentlyViewedProducts();

    if (!recentProducts || recentProducts.length === 0) {
      container.innerHTML = `
        <h3 class="recently-viewed__title">${window.theme?.strings?.recentlyViewed || 'Recently Viewed'}</h3>
        <p class="recently-viewed__empty">${window.theme?.strings?.recentlyViewedEmpty || 'Visita un prodotto per vederlo qui.'}</p>
      `;
      container.style.display = '';
      return;
    }

    container.innerHTML = `
      <h3 class="recently-viewed__title">${window.theme?.strings?.recentlyViewed || 'Recently Viewed'}</h3>
      <div class="recently-viewed__products">
        ${recentProducts.map(product => `
          <a href="${product.url}" class="recently-viewed__product">
            ${product.image ? `<img src="${window.bfyImg(product.image, 300)}" alt="${product.title}" loading="lazy">` : ''}
            <span class="recently-viewed__product-title">${product.title || 'Product'}</span>
            ${product.price ? `<span class='recently-viewed__product-price'>${product.price}</span>` : ''}
          </a>
        `).join('')}
      </div>`;
    container.style.display = '';
  }

  getRecentlyViewedProducts() {
    try {
      const limit = this.config.recentlyViewedLimit || 4;
      const richRaw = localStorage.getItem('beezfy_recently_viewed');
      if (richRaw) {
        const arr = JSON.parse(richRaw).filter(p => p && p.url);
        if (arr.length) return arr.slice(0, limit);
      }

      // Remove legacy numeric-only storage to avoid showing IDs
      const legacy = localStorage.getItem('beezfy-recently-viewed');
      if (legacy) {
        try {
          const parsedLegacy = JSON.parse(legacy);
          if (Array.isArray(parsedLegacy)) {
            localStorage.removeItem('beezfy-recently-viewed');
          }
        } catch (err) {
          localStorage.removeItem('beezfy-recently-viewed');
        }
      }

      // Do NOT render numeric-only fallbacks; better to show placeholder
      return [];
    } catch (e) {
      console.warn('[BeezFy Menu] Failed to parse recently viewed', e);
      return [];
    }
  }

  setupEventListeners() {
    // Menu triggers
    // If the unified drawer system is active, let it own trigger clicks
    if (!window.beezfyDrawerSystem) {
      this.elements.triggers?.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });
    }

    // Close button
    this.elements.closeBtn?.addEventListener('click', () => this.close());

    // Overlay click (fallback only; unified system already handles it)
    if (!window.beezfyDrawerSystem) {
      this.elements.overlay?.addEventListener('click', () => this.close());
    }

    // Sync state with unified drawer system events
    document.addEventListener('drawer:opened', (e) => {
      if (e.detail?.type === 'menu') {
        this.state.isOpen = true;
        this.addRecentlyViewed();
        this.updateNavOffset();
        // Focus first element when drawer opens
        setTimeout(() => {
          const firstFocusable = this.elements.drawer?.querySelector('button, a, input');
          firstFocusable?.focus();
        }, this.config.animationDuration);
      }
    });

    document.addEventListener('drawer:closed', (e) => {
      if (e.detail?.type === 'menu') {
        this.state.isOpen = false;
        this.resetNavigation();
      }
    });

    // Back button
    this.elements.backButton?.addEventListener('click', () => this.navigateBack());

    // Submenu navigation
    this.setupSubmenuNavigation();

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.close();
      }
    });

    // Prevent body scroll when menu is open
    this.elements.drawer?.addEventListener('touchmove', (e) => {
      if (this.state.isOpen && !this.isScrollable(e.target)) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  setupSubmenuNavigation() {
    // ACCORDION pulito: apre/chiude al click, una sola voce aperta per livello, nessun effetto collaterale.
    var nav = this.elements.nav;
    if (!nav) return;

    var childSubmenu = function (el) {
      return Array.prototype.find.call(el.children || [], function (c) {
        return c.classList && (c.classList.contains('menu-drawer__submenu') || c.classList.contains('menu-drawer__submenu--nested'));
      });
    };
    var close = function (sm) {
      if (!sm) return;
      sm.classList.remove('is-active', 'is-visible');
      sm.setAttribute('aria-hidden', 'true');
      var item = sm.parentElement;
      var tr = item && item.querySelector('[data-menu-trigger]');
      if (tr) { tr.setAttribute('aria-expanded', 'false'); tr.classList.remove('is-open'); }
      // chiudi anche eventuali nidificati aperti
      Array.prototype.forEach.call(sm.querySelectorAll('.menu-drawer__submenu.is-active, .menu-drawer__submenu--nested.is-active'), function (n) {
        n.classList.remove('is-active', 'is-visible'); n.setAttribute('aria-hidden', 'true');
        var t = n.parentElement && n.parentElement.querySelector('[data-menu-trigger]');
        if (t) { t.setAttribute('aria-expanded', 'false'); t.classList.remove('is-open'); }
      });
    };

    nav.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-menu-trigger]');
      if (!trigger || !nav.contains(trigger)) return;
      e.preventDefault();
      e.stopPropagation();
      var item = trigger.parentElement;
      var submenu = childSubmenu(item);
      if (!submenu) return;
      var willOpen = !submenu.classList.contains('is-active');

      // chiudi i fratelli allo stesso livello
      var parent = item.parentElement;
      if (parent) {
        Array.prototype.forEach.call(parent.children, function (sib) {
          if (sib === item) return;
          var sm = childSubmenu(sib);
          if (sm && sm.classList.contains('is-active')) close(sm);
        });
      }

      if (willOpen) {
        submenu.classList.add('is-active', 'is-visible');
        submenu.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        trigger.classList.add('is-open');
      } else {
        close(submenu);
      }
    });
  }

  navigateToSubmenu(submenu, title, isNested = false, triggerEl = null) {
    if (this.state.isAnimating) return;
    if (!submenu) return;

    this.state.isAnimating = true;

    try {
      // Store navigation history
      this.state.history.push({
        level: this.state.currentLevel,
        title: title,
        element: submenu,
        isNested: isNested,
        trigger: triggerEl || null
      });

      const heading = submenu.querySelector('[data-submenu-heading]');
      if (heading) {
        heading.textContent = title;
      }

      // Update level
      this.state.currentLevel++;

      // Show and update back button
      if (this.elements.backButton) {
        this.elements.backButton.classList.add('is-visible');
        const backText = this.elements.backButton.querySelector('[data-back-text]');
        if (backText) {
          backText.textContent = title;
        }
      }

      // Hide main nav
      if (this.elements.nav) {
        this.elements.nav.classList.add('is-hidden');
      }

      // Animate transition
      this.animateMenuTransition(submenu, 'forward');

      // Accessibility: ensure the submenu is not hidden to assistive tech
      submenu.setAttribute('aria-hidden', 'false');

      // Update active state
      if (isNested) {
        this.state.activeNestedSubmenu = submenu;
      } else {
        this.state.activeSubmenu = submenu;
      }
    } catch (error) {
      console.error('[BeezFy Menu] Error navigating to submenu:', error);
      // Rollback state
      this.state.currentLevel--;
      this.state.history.pop();
    } finally {
      // Always reset animation state
      setTimeout(() => {
        this.state.isAnimating = false;
      }, this.config.animationDuration);
    }
  }

  navigateBack() {
    if (this.state.history.length === 0 || this.state.isAnimating) return;

    this.state.isAnimating = true;

    try {
      const previous = this.state.history.pop();
      this.state.currentLevel--;

      // Update or hide back button
      if (this.state.history.length > 0) {
        if (this.elements.backButton) {
          const prevTitle = this.state.history[this.state.history.length - 1].title;
          const backText = this.elements.backButton.querySelector('[data-back-text]');
          if (backText) {
            backText.textContent = prevTitle;
          }
        }
      } else {
        // Back to root
        if (this.elements.backButton) {
          this.elements.backButton.classList.remove('is-visible');
        }
        // Show main nav when going back to root
        if (this.elements.nav) {
          this.elements.nav.classList.remove('is-hidden');
        }
      }

      // Animate transition
      if (previous && previous.element) {
        this.animateMenuTransition(previous.element, 'backward');
        // Hide from a11y tree
        previous.element.setAttribute('aria-hidden', 'true');
      }

      // Update active state
      if (previous) {
        if (previous.isNested) {
          this.state.activeNestedSubmenu = null;
        } else {
          this.state.activeSubmenu = null;
          this.state.activeNestedSubmenu = null;
        }
        // Restore ARIA expanded on trigger
        if (previous.trigger) {
          previous.trigger.setAttribute('aria-expanded', 'false');
          previous.trigger.focus();
        }
      }
    } catch (error) {
      console.error('[BeezFy Menu] Error navigating back:', error);
    } finally {
      // Always reset animation state
      setTimeout(() => {
        this.state.isAnimating = false;
      }, this.config.animationDuration);
    }
  }

  animateMenuTransition(element, direction) {
    const content = this.elements.content;

    if (direction === 'forward') {
      // Show submenu with animation
      element.classList.add('is-active');

      // Force reflow
      element.offsetHeight;

      // Slide in submenu
      requestAnimationFrame(() => {
        element.classList.add('is-visible');
      });

      // Scroll to top
      if (content) {
        content.scrollTop = 0;
      }
    } else {
      // Hide submenu with animation
      element.classList.remove('is-visible');

      setTimeout(() => {
        element.classList.remove('is-active');
      }, this.config.animationDuration);
    }
  }

  setupTouchGestures() {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    this.elements.drawer?.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    this.elements.drawer?.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].clientX;
      touchEndY = e.changedTouches[0].clientY;

      this.handleSwipe(touchStartX, touchEndX, touchStartY, touchEndY);
    }, { passive: true });
  }

  handleSwipe(startX, endX, startY, endY) {
    const diffX = startX - endX;
    const diffY = startY - endY;

    // Check if horizontal swipe
    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (Math.abs(diffX) > this.config.swipeThreshold) {
        if (diffX > 0) {
          // Swipe left - close menu
          if (this.state.currentLevel === 0) {
            this.close();
          }
        } else {
          // Swipe right - go back
          if (this.state.currentLevel > 0) {
            this.navigateBack();
          }
        }
      }
    }
  }

  setupSearch() {
    if (!this.elements.searchInput) return;

    let searchTimeout;

    this.elements.searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      this.state.searchQuery = e.target.value;

      if (this.state.searchQuery.length > 2) {
        searchTimeout = setTimeout(() => {
          this.performSearch(this.state.searchQuery);
        }, this.config.searchDebounce);
      } else {
        this.hideSearchResults();
      }
    });

    // Close search results
    this.elements.searchResults?.querySelector('.search-results__close')
      ?.addEventListener('click', () => this.hideSearchResults());
  }

  async performSearch(query) {
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
      const data = await response.json();

      if (data.resources?.results?.products) {
        this.showSearchResults(data.resources.results.products);
      }
    } catch (error) {
      this.hideSearchResults();
    }
  }

  showSearchResults(products) {
    if (!this.elements.searchResults) return;

    const content = this.elements.searchResults.querySelector('.search-results__content');

    if (products.length > 0) {
      content.innerHTML = products.map(product => `
        <a href="${product.url}" class="search-result__item">
          <img src="${window.bfyImg(product.featured_image, 300)}" alt="${product.title}" loading="lazy">
          <div class="search-result__details">
            <span class="search-result__title">${product.title}</span>
            <span class="search-result__price">${this.formatPrice(product.price)}</span>
          </div>
        </a>
      `).join('');
    } else {
      content.textContent = '';
      const p = document.createElement('p');
      p.className = 'search-results__empty';
      p.textContent = window.theme?.strings?.noResults || 'No results found';
      content.appendChild(p);
    }

    this.elements.searchResults.style.display = 'block';
  }

  hideSearchResults() {
    if (this.elements.searchResults) {
      this.elements.searchResults.style.display = 'none';
    }
  }

  formatPrice(cents) {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || window.Shopify.money_format);
    return `€${(cents / 100).toFixed(2).replace('.', ',')}`; // mai dollari
  }

  setupAccountFeatures() {
    // Add wishlist count if available
    const wishlistCount = window.BeezfyWishlist?.getCount?.() || 0;

    if (wishlistCount > 0) {
      const wishlistLink = document.createElement('a');
      wishlistLink.href = '/pages/wishlist';
      wishlistLink.className = 'menu-drawer__account-link menu-drawer__wishlist';
      wishlistLink.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
        <span>${window.theme?.strings?.wishlist || 'Wishlist'} (${wishlistCount})</span>
      `;

      this.elements.account?.insertBefore(wishlistLink, this.elements.account.firstChild);
    }
  }

  setupLocalization() {
    // Language selector
    const langSelector = this.elements.localization?.querySelector('[data-language-selector]');

    langSelector?.addEventListener('change', (e) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'locale_code';
      input.value = e.target.value;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    });

    // Currency selector
    const currencySelector = this.elements.localization?.querySelector('[data-currency-selector]');

    currencySelector?.addEventListener('change', (e) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'country_code';
      input.value = e.target.value;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    });
  }

  preloadImages() {
    if (!this.config.enableCategoryImages || !window.beezfySettings?.menuCategoryImages) return;

    const images = Object.values(window.beezfySettings.menuCategoryImages);
    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }

  initializeAccessibility() {
    // Set ARIA attributes
    this.elements.drawer?.setAttribute('role', 'navigation');
    this.elements.drawer?.setAttribute('aria-label', 'Mobile menu');

    // Trap focus when menu is open
    this.elements.drawer?.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.state.isOpen) {
        const focusableElements = this.elements.drawer.querySelectorAll(
          'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });
  }

  setupDarkMode() {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    
    const applyDarkMode = (isDark) => {
      if (isDark) {
        this.elements.drawer?.setAttribute('data-theme', 'dark');
      } else {
        this.elements.drawer?.removeAttribute('data-theme');
      }
    };

    applyDarkMode(prefersDark.matches);

    prefersDark.addEventListener('change', (e) => {
      applyDarkMode(e.matches);
    });
  }

  isScrollable(element) {
    let el = element;
    while (el && el !== this.elements.drawer) {
      if (el.scrollHeight > el.clientHeight) {
        return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  open() {
    if (this.state.isOpen || this.state.isAnimating) return;

    this.state.isAnimating = true;
    this.state.isOpen = true;

    // Use drawer system if available
    if (window.beezfyDrawerSystem) {
      window.beezfyDrawerSystem.openDrawer('menu');
    } else {
      // Fallback
      this.elements.drawer?.classList.add('is-open');
      this.elements.overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    this.updateNavOffset();
    this.addRecentlyViewed();

    // Emit event
    window.dispatchEvent(new CustomEvent('beezfy:menu:open'));

    // Focus on first focusable element
    setTimeout(() => {
      const firstFocusable = this.elements.drawer?.querySelector('button, a, input');
      firstFocusable?.focus();
      this.state.isAnimating = false;
    }, this.config.animationDuration);
  }

  close() {
    if (!this.state.isOpen || this.state.isAnimating) return;

    this.state.isAnimating = true;
    this.state.isOpen = false;

    // Reset navigation
    this.resetNavigation();

    // Use drawer system if available
    if (window.beezfyDrawerSystem) {
      window.beezfyDrawerSystem.closeDrawer('menu');
    } else {
      // Fallback
      this.elements.drawer?.classList.remove('is-open');
      this.elements.overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    // Emit event
    window.dispatchEvent(new CustomEvent('beezfy:menu:close'));

    setTimeout(() => {
      this.state.isAnimating = false;
    }, this.config.animationDuration);
  }

  resetNavigation() {
    // Reset all submenus
    this.state.history = [];
    this.state.currentLevel = 0;
    this.state.activeSubmenu = null;
    this.state.activeNestedSubmenu = null;

    // Hide back button
    if (this.elements.backButton) {
      this.elements.backButton.classList.remove('is-visible');
    }

    // Show main nav
    if (this.elements.nav) {
      this.elements.nav.classList.remove('is-hidden');
    }

    // Hide all submenus
    const allSubmenus = this.elements.drawer?.querySelectorAll('.menu-drawer__submenu');
    allSubmenus?.forEach(submenu => {
      submenu.classList.remove('is-active', 'is-visible');
      submenu.setAttribute('aria-hidden', 'true');
    });

    // Reset ARIA on all triggers
    this.elements.drawer?.querySelectorAll('[aria-expanded="true"]').forEach(el => {
      el.setAttribute('aria-expanded', 'false');
    });

    // Hide search results
    this.hideSearchResults();

    // Clear search input
    if (this.elements.searchInput) {
      this.elements.searchInput.value = '';
      this.state.searchQuery = '';
    }

    // Scroll to top
    if (this.elements.content) {
      this.elements.content.scrollTop = 0;
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BeezfyMobileMenu = new BeezfyMobileMenuPremium();
  });
} else {
  window.BeezfyMobileMenu = new BeezfyMobileMenuPremium();
}
